import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { XIcon } from "lucide-react";
import { isSelectOverlayTarget } from "./overlay-interactions";
import { overlayScrimClassName } from "./overlay-scrim";
import { bottomSheetGrabberClassName } from "./bottom-sheet-surface";
import { useIsMobile } from "../../lib/use-mobile";
import { MOBILE_SHEET_HOST_GROW_CLASS, MobileSheetHost } from "./mobile-sheet-host";

/**
 * Phone: every dialog is the app's bottom sheet — full width, anchored to the bottom with the large top radius
 * and the grabber, sliding up, capped below the status bar and clear of the home indicator. Applied after the
 * caller's classes so a desktop width (`max-w-md`, `sm:max-w-lg`) never narrows the sheet.
 */
const MOBILE_SHEET_CLASSNAME =
  "top-auto bottom-0 left-0 right-0 w-full max-w-none sm:max-w-none translate-x-0 translate-y-0 max-h-[92dvh] rounded-t-[2rem] rounded-b-none border-0 pb-[env(safe-area-inset-bottom)] duration-300 data-open:slide-in-from-bottom data-closed:slide-out-to-bottom data-open:zoom-in-100 data-closed:zoom-out-100";

const DIALOG_CONTENT_SIZES = {
  xs: "sm:max-w-xs",
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  "5xl": "sm:max-w-5xl",
  full: "sm:max-w-[calc(100%-2rem)]",
} as const;

type DialogContentSize = keyof typeof DIALOG_CONTENT_SIZES;

function isDialogSlotElement(child: React.ReactElement, component: React.ElementType, displayName: string) {
  if (child.type === component) return true;
  if (typeof child.type === "function" && (child.type as { displayName?: string }).displayName === displayName) {
    return true;
  }
  return false;
}

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return (
    <DialogPrimitive.Portal {...props}>
      <div data-slot='dialog-portal'>{props.children}</div>
    </DialogPrimitive.Portal>
  );
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot='dialog-overlay'
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 isolate z-50 duration-100",
        overlayScrimClassName,
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  size = "lg",
  showCloseButton = true,
  fullHeight = false,
  bodyClassName,
  mobileFullscreen: _mobileFullscreen,
  mobilePresentation: _mobilePresentation,
  mobileHandle: _mobileHandle,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  size?: DialogContentSize;
  showCloseButton?: boolean;
  fullHeight?: boolean;
  /** Extra classes for the body wrapper (padding / gap), for compact dialogs that lay out their own content. */
  bodyClassName?: string;
  /** @deprecated Ignored. On a phone every dialog is a bottom sheet. */
  mobileHandle?: boolean;
  /** @deprecated Ignored. On a phone every dialog is a bottom sheet. */
  mobileFullscreen?: boolean;
  /** @deprecated Ignored. On a phone every dialog is a bottom sheet. */
  mobilePresentation?: "sheet" | "fullscreen";
}) {
  void _mobileFullscreen;
  void _mobilePresentation;
  void _mobileHandle;

  const headerNodes: React.ReactElement[] = [];
  const footerNodes: React.ReactElement[] = [];
  const bodyNodes: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      bodyNodes.push(child);
      return;
    }

    if (isDialogSlotElement(child, DialogHeader, "DialogHeader")) {
      headerNodes.push(child);
      return;
    }

    if (isDialogSlotElement(child, DialogFooter, "DialogFooter")) {
      footerNodes.push(child);
      return;
    }

    bodyNodes.push(child);
  });

  const hasBodyNodes = bodyNodes.length > 0;
  const isMobile = useIsMobile();

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot='dialog-content'
        data-full-height={fullHeight ? "true" : undefined}
        className={cn(
          "bg-card data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border/35 text-sm shadow-[var(--shadow-dialog)] duration-100",
          DIALOG_CONTENT_SIZES[size],
          className,
          isMobile && MOBILE_SHEET_CLASSNAME,
          isMobile && MOBILE_SHEET_HOST_GROW_CLASS,
        )}
        onInteractOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
            return;
          }

          onInteractOutside?.(event);
        }}
        onPointerDownOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
          }
        }}
        {...props}
      >
        {isMobile ? <div aria-hidden className={bottomSheetGrabberClassName} /> : null}
        <MaybeSheetHost enabled={isMobile}>
        {headerNodes}
        {hasBodyNodes ? (
          <div data-slot='dialog-scroll-area' className='flex-1 overflow-y-auto overscroll-none'>
            <div
              data-slot='dialog-body'
              className={cn(
                "grid grid-cols-1 min-w-0 gap-4 px-4 py-4 [&_[data-slot=dialog-header]]:mx-[-1rem] [&_[data-slot=dialog-header]]:mt-[-1rem] [&_[data-slot=dialog-footer]]:mx-[-1rem] [&_[data-slot=dialog-footer]]:mb-[-1rem]",
                bodyClassName,
              )}
            >
              {bodyNodes}
            </div>
          </div>
        ) : null}
        {footerNodes}
        </MaybeSheetHost>
        {showCloseButton && (
          <DialogPrimitive.Close data-slot='dialog-close' asChild>
            <Button variant='ghost' className='absolute top-2 right-2 z-30 size-8' size='icon-sm'>
              <XIcon />
              <span className='sr-only'>Close</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/** A phone dialog is a sheet, so it hosts the pickers opened inside it (see `MobileSheetHost`). */
function MaybeSheetHost({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  return enabled ? <MobileSheetHost>{children}</MobileSheetHost> : <>{children}</>;
}

/**
 * Reusable modal chrome: no tinted header band — only a bottom border. Footer is optional (`DialogFooter`).
 * Prefer `showCloseButton` on the header (and `showCloseButton={false}` on `DialogContent`) so title and X share one row.
 */
function DialogHeader({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & { showCloseButton?: boolean }) {
  if (showCloseButton) {
    return (
      <div
        data-slot='dialog-header'
        className={cn(
          "sticky top-0 z-20 flex flex-row items-start justify-between gap-4 border-b border-border/35 bg-card px-4 py-3 text-left",
          className,
        )}
        {...props}
      >
        <div className='flex min-w-0 flex-1 flex-col gap-2'>{children}</div>
        <DialogPrimitive.Close data-slot='dialog-close' asChild>
          <Button variant='ghost' className='relative z-10 size-8 shrink-0' size='icon-sm'>
            <XIcon />
            <span className='sr-only'>Close</span>
          </Button>
        </DialogPrimitive.Close>
      </div>
    );
  }

  return (
    <div
      data-slot='dialog-header'
      className={cn("sticky top-0 z-20 flex flex-col gap-2 border-b border-border/35 bg-card px-4 py-3 text-left", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        "border-border/35 bg-card mt-auto flex shrink-0 flex-row items-center justify-end gap-2 border-t px-4 py-4",
        "sticky bottom-0 z-20",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant='outline'>Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

DialogHeader.displayName = "DialogHeader";
DialogFooter.displayName = "DialogFooter";

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn("text-base leading-none font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-sm leading-snug *:[a]:underline *:[a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

export {
  DIALOG_CONTENT_SIZES,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};

export type { DialogContentSize };
